import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';


const emptyLine = () => ({ accountId: '', description: '', quantity: 1, unitPrice: '' });

export default function AddInvoice() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ invoiceNumber: '', contactId: '', companyId: '', issueDate: new Date().toISOString().split('T')[0], dueDate: '', status: 'DRAFT', notes: '' });
  const [lines, setLines] = useState([emptyLine()]);

  useEffect(() => {
    api(`/accounts`).then(r => r.json()).then(setAccounts).catch(() => {});
    api(`/contacts`).then(r => r.json()).then(setContacts).catch(() => {});
    api(`/companies`).then(r => r.json()).then(setCompanies).catch(() => {});
  }, []);

  const updateLine = (i, field, value) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: value };
    setLines(updated);
  };

  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const total = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      contactId: form.contactId || null,
      companyId: form.companyId || null,
      lines: lines.filter(l => l.accountId).map(l => ({
        accountId: l.accountId, description: l.description,
        quantity: parseFloat(l.quantity) || 1, unitPrice: parseFloat(l.unitPrice) || 0,
      })),
    };
    const res = await api(`/invoices`, { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      const inv = await res.json();
      navigate(`/accounting/invoices/${inv.id}`);
    }
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">New Invoice</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice # *</label>
              <input type="text" required value={form.invoiceNumber} onChange={(e) => setForm({...form, invoiceNumber: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
              <select value={form.contactId} onChange={(e) => setForm({...form, contactId: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600">
                <option value="">None</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select value={form.companyId} onChange={(e) => setForm({...form, companyId: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600">
                <option value="">None</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
              <input type="date" required value={form.issueDate} onChange={(e) => setForm({...form, issueDate: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
              <input type="date" required value={form.dueDate} onChange={(e) => setForm({...form, dueDate: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600">
                <option value="DRAFT">Draft</option>
                <option value="SENT">Sent</option>
                <option value="PAID">Paid</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
              <button type="button" onClick={addLine} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">+ Add Line</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2">
                        <select value={line.accountId} onChange={(e) => updateLine(i, 'accountId', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
                          <option value="">Select account</option>
                          {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2"><input type="text" value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" /></td>
                      <td className="px-4 py-2"><input type="number" step="0.01" min="0" value={line.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" /></td>
                      <td className="px-4 py-2"><input type="number" step="0.01" min="0" value={line.unitPrice} onChange={(e) => updateLine(i, 'unitPrice', e.target.value)} className="w-28 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" /></td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{((parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0)).toFixed(2)}</td>
                      <td className="px-4 py-2">{lines.length > 1 && <button type="button" onClick={() => removeLine(i)} className="text-red-500 hover:text-red-700 text-sm">✕</button>}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={4} className="px-4 py-2 text-right text-sm">Total</td>
                    <td className="px-4 py-2 text-right font-mono text-sm">{total.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => navigate('/accounting/invoices')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Save</button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
